import { Box } from "@mui/material";
import BreakdownBars from "../components/BreakdownBars";
import Card, { CardGrid } from "../components/Card";
import KpiTile from "../components/KpiTile";
import SeriesChart from "../components/SeriesChart";
import StatTable from "../components/StatTable";
import StatsGate from "../components/StatsGate";
import { formatPercent } from "../format";
import { Safety, TabProps } from "../types";
import { useStats } from "../useStats";

export default function SafetyTab({ query, reloadKey, gameId }: TabProps) {
  const state = useStats<Safety>("/safety", query, reloadKey);
  // Moderation actions and escalations are not scoped to a single game, so
  // say so once a game id filters everything else on this tab.
  const suffix = gameId ? " (all games)" : "";
  return (
    <StatsGate state={state}>
      {(data, envelope) => (
        <>
          <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: "1fr", sm: "repeat(4, 1fr)" }, mb: 2 }}>
            <KpiTile label="Open reports now" value={data.openReportsNow} />
            <KpiTile label="Messages flagged" value={data.flaggedMessages.flagRate} format={formatPercent} />
            <KpiTile label="Banned now" value={data.bannedNow} />
            <KpiTile label="Muted now" value={data.mutedNow} />
          </Box>
          <CardGrid>
            <Card title="Reports and moderation actions" wide>
              <SeriesChart points={data.series} bucket={envelope.filters.bucket ?? "day"}
                lines={[{ key: "reports", label: "Reports" }, { key: "moderationActions", label: `Moderation actions${suffix}` }]} />
            </Card>
            <Card title="What was reported"><BreakdownBars rows={data.reportsByTargetType} /></Card>
            <Card title={`Moderation actions${suffix}`}><BreakdownBars rows={data.actionsByType} /></Card>
            <Card title={`Escalations by status${suffix}`}><BreakdownBars rows={data.escalations.byStatus} /></Card>
            <Card title={`Escalations by channel${suffix}`}><BreakdownBars rows={data.escalations.byChannel} /></Card>
            <Card title="Most reported games" wide>
              <StatTable
                rows={data.topReportedGames} getRowId={(r) => `${r.tenant}:${r.gameId}`}
                columns={[
                  { field: "tenant", headerName: "Tenant", flex: 1 },
                  { field: "gameId", headerName: "Game", flex: 1 },
                  { field: "reports", headerName: "Reports", type: "number", flex: 1 },
                ]}
              />
            </Card>
            <Card title="Moderator workload" wide>
              <StatTable
                rows={data.actionsByModerator} getRowId={(r) => r.moderatorUserId}
                columns={[
                  { field: "displayName", headerName: "Moderator", flex: 2, valueFormatter: (v: string | null) => v ?? "n/a" },
                  { field: "tenant", headerName: "Tenant", flex: 1 },
                  { field: "actions", headerName: "Actions", type: "number", flex: 1 },
                ]}
              />
            </Card>
          </CardGrid>
        </>
      )}
    </StatsGate>
  );
}
